import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import {
	updateAllowedWorkCentersForShifts,
	updateCompanySettings,
	updateLocationData,
} from "../../../../../../../export/gat-admin";
import useGatAdminConfigData from "../../../../../../../ts/components/gat-admin/controllers/use-gat-admin-config-data";
import { findDifferentKeys } from "../../../../utils";

const shiftObj: any = {
	swapEnabled: "shift.swapEnabled",
	swapApprovalRequired: "shift.swapApprovalRequired",
	cancelEnabled: "shift.cancelEnabled",
	forceCancelEnabled: "shift.forceCancelEnabled",
	cancelWindowBeforeStartDays: "shift.cancelWindowBeforeStartDays",
	cancelWindowAfterAcceptHrs: "shift.cancelWindowAfterAcceptHrs",
	jobExpiryEnabled: "clientApp.jobExpiryEnabled",
	minWorkHoursRequired: "jobExpiry.minWorkHoursRequired",
	minWorkHoursDays: "jobExpiry.minWorkHoursDays",
	shiftPurpose: "clientApp.showShiftPurpose.enabled",
	overlappingShiftAllowed: "overlappingShiftSingup.Enabled",
	overlapWindowTime: "overlappingShiftSingup.maxAllowedTime",
	// Note: It is not about consecutive shifts, it is a gap between two shifts. API key based on BE.
	minShiftsGapEnabled: "consecutiveShiftGap.enabled",
	minShiftsGapHours: "consecutiveShiftGap.minHours",
	minShiftsGapSkipEnabled: "consecutiveShiftGap.skipGap.enabled",
	minShiftsGapSkipMinutes: "consecutiveShiftGap.skipGap.maxMinutes",
	partialShiftAllowed: "partialShiftSignup.enabled",
	minPartialShiftHours: "partialShiftSignup.minShiftHours",
	flexiShiftAllowed: "flexiShiftSignup.enabled",
	minFlexiShiftHours: "flexiShiftSignup.minShiftHours",
	shiftWorkerTypeFilterEnabled: "shift.workerTypeFilter.enabled",
	minWeekHoursNotifEnabled: "minWeekHoursNotif.enabled",
	minWeekHoursNotifTriggerDays: "minWeekHoursNotif.triggerDays",
	acceptanceCutoffEnabled: "shiftAcceptanceCutoff.enabled",
	acceptanceCutoffMinHours: "shiftAcceptanceCutoff.minHours",
	pastDatedTimeOffAllowed: "timeOff.allowPastDates",
	shiftChangeEnabled: "shiftChange.enabled",
	shiftChangeMaxPerMonth: "shiftChange.maxPerMonth",
	shiftChangeMinNoticeHours: "shiftChange.minNoticeHours",
};

// GAT-7970 shift change request settings. Defaults match the API's.
export const SHIFT_CHANGE_DEFAULT_MAX_PER_MONTH = 1;
export const SHIFT_CHANGE_DEFAULT_MIN_NOTICE_HOURS = 0;
const SHIFT_CHANGE_SETTING_TYPES: Record<string, string> = {
	"shiftChange.enabled": "System.Boolean",
	"shiftChange.maxPerMonth": "System.Int32",
	"shiftChange.minNoticeHours": "System.Int32",
};

// Setting values arrive as booleans/numbers or as their string forms.
const toBoolean = (value: unknown) =>
	value === true || String(value).toLowerCase() === "true";
const toInteger = (value: unknown, fallback: number) => {
	const parsed = Number(value);
	return value === null || value === undefined || value === "" ||
		!Number.isFinite(parsed)
		? fallback
		: parsed;
};

const initialShiftSettings = {
	swapEnabled: false,
	swapApprovalRequired: false,
	cancelEnabled: false,
	forceCancelEnabled: false,
	cancelWindowBeforeStartDays: 0,
	cancelWindowAfterAcceptHrs: 0,
	jobExpiryEnabled: false,
	minWorkHoursRequired: 0,
	minWorkHoursDays: 0,
	forceAssignShift: false,
	forceAssignShiftBeforeStartHours: 0,
	shiftPurpose: false,
	overlappingShiftAllowed: false,
	overlapWindowTime: 30,
	minShiftsGapEnabled: false,
	minShiftsGapHours: 1,
	minShiftsGapSkipEnabled: false,
	minShiftsGapSkipMinutes: 0,
	partialShiftAllowed: false,
	minPartialShiftHours: null,
	flexiShiftAllowed: false,
	minFlexiShiftHours: null,
	shiftWorkerTypeFilterEnabled: false,
	minWeekHoursNotifEnabled: false,
	minWeekHoursNotifTriggerDays: [],
	acceptanceCutoffEnabled: false,
	acceptanceCutoffMinHours: null,
	pastDatedTimeOffAllowed: false,
	shiftChangeEnabled: false,
	shiftChangeMaxPerMonth: SHIFT_CHANGE_DEFAULT_MAX_PER_MONTH,
	shiftChangeMinNoticeHours: SHIFT_CHANGE_DEFAULT_MIN_NOTICE_HOURS,
};

interface WorkCenter {
	id: string | number;
	name: string;
	isOverlapSignupEnabled: boolean;
	isPartialSignupEnabled: boolean;
	isFlexiSignupEnabled: boolean;
	isShiftAcceptanceCutoffEnabled: boolean;
}

const daysOfWeek = [
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
	"Sunday",
];

const useShiftSettingsController = (entity: any, locationData: any) => {
	const dispatch = useDispatch();
	const {
		updateCompanySettingsStatus,
		updateLocationDataStatus,
		companySettingsObj,
		locationSettingsObj,
		currentLocationData,
		allSettings,
		updateAllowedWorkCentersForShiftsStatus,
		minShiftDurationHours,
		companyId,
		getCompanySettingsStatus,
		getLocationSettingsStatus,
	} = useGatAdminConfigData();

	const [loading, setLoading] = useState(false);

	const [shiftSettings, setShiftSettings] = useState<any>(initialShiftSettings);
	const [newShiftSettings, setNewShiftSettings] = useState<any>({});

	const [shiftSettingsLocationData, setShiftSettingsLocationData] =
		useState<any>(locationData);
	const [newShiftSettingsLocationData, setNewShiftSettingsLocationData] =
		useState<any>({});

	const [overlapWorkcenters, setOverlapWorkcenters] = useState<any>([]);
	const [newOverlapWorkcenters, setNewOverlapWorkcenters] = useState<any>([]);

	const [partialShiftWorkcenters, setPartialShiftWorkcenters] = useState<any>(
		[]
	);
	const [newPartialShiftWorkcenters, setNewPartialShiftWorkcenters] =
		useState<any>([]);

	const [flexiShiftWorkcenters, setFlexiShiftWorkcenters] = useState<any>([]);
	const [newFlexiShiftWorkcenters, setNewFlexiShiftWorkcenters] =
		useState<any>([]);

	const [acceptanceCutoffWorkcenters, setAcceptanceCutoffWorkcenters] =
		useState<any>([]);
	const [newAcceptanceCutoffWorkcenters, setNewAcceptanceCutoffWorkcenters] =
		useState<any>([]);

	const settingsObj =
		entity === "location" ? locationSettingsObj : companySettingsObj;

	const { locationWorkcenters } = useMemo(() => {
		if (!currentLocationData?.workCenters) {
			return { locationWorkcenters: [] };
		}
		return {
			locationWorkcenters: currentLocationData.workCenters,
		};
	}, [currentLocationData?.workCenters]);

	useEffect(() => {
		if (locationWorkcenters) {
			const overlapWorkcenters = locationWorkcenters
				?.filter(
					(wc: { isOverlapSignupEnabled: boolean }) => wc.isOverlapSignupEnabled
				)
				.map((wc: { id: number }) => wc.id);

			const partialShiftWorkcenters = locationWorkcenters
				?.filter(
					(wc: { isPartialSignupEnabled: boolean }) => wc.isPartialSignupEnabled
				)
				.map((wc: { id: number }) => wc.id);

			const flexiShiftWorkcenters = locationWorkcenters
				?.filter(
					(wc: { isFlexiSignupEnabled: boolean }) => wc.isFlexiSignupEnabled
				)
				.map((wc: { id: number }) => wc.id);

			const acceptanceCutoffWorkcenters = locationWorkcenters
				?.filter(
					(wc: { isShiftAcceptanceCutoffEnabled: boolean }) =>
						wc.isShiftAcceptanceCutoffEnabled
				)
				.map((wc: { id: number }) => wc.id);

			setOverlapWorkcenters(overlapWorkcenters);
			setNewOverlapWorkcenters(overlapWorkcenters);
			setPartialShiftWorkcenters(partialShiftWorkcenters);
			setNewPartialShiftWorkcenters(partialShiftWorkcenters);
			setFlexiShiftWorkcenters(flexiShiftWorkcenters);
			setNewFlexiShiftWorkcenters(flexiShiftWorkcenters);
			setAcceptanceCutoffWorkcenters(acceptanceCutoffWorkcenters);
			setNewAcceptanceCutoffWorkcenters(acceptanceCutoffWorkcenters);
		}
	}, [locationWorkcenters, newShiftSettingsLocationData]);

	const handleSave = async () => {
		try {
			setLoading(true);

			const stringifiedNewOverlap = JSON.stringify(newOverlapWorkcenters);
			const stringifiedOverlap = JSON.stringify(overlapWorkcenters);

			const stringifiedNewPartialShift = JSON.stringify(
				newPartialShiftWorkcenters
			);
			const stringifiedPartialShift = JSON.stringify(partialShiftWorkcenters);

			const stringifiedNewFlexiShift = JSON.stringify(
				newFlexiShiftWorkcenters
			);
			const stringifiedFlexiShift = JSON.stringify(flexiShiftWorkcenters);

			const stringifiedNewAcceptanceCutoff = JSON.stringify(
				newAcceptanceCutoffWorkcenters
			);
			const stringifiedAcceptanceCutoff = JSON.stringify(
				acceptanceCutoffWorkcenters
			);

			const changedSettings = findDifferentKeys(
				shiftSettings,
				newShiftSettings
			);
			const changedValues = changedSettings
				.map((key) => {
					let item = shiftObj[key];
					if (entity === "company") {
						if (key === "flexiShiftAllowed") {
							item = "company.flexiShiftSignup.enabled";
						}
						if (key === "minFlexiShiftHours") {
							item = "company.flexiShiftSignup.minShiftHours";
						}
					}
					let obj = settingsObj[item] || allSettings[item];

					if (
						item === "minWeekHoursNotif.enabled" ||
						item === "minWeekHoursNotif.triggerDays" ||
						item === "flexiShiftSignup.enabled" ||
						item === "flexiShiftSignup.minShiftHours" ||
						item === "company.flexiShiftSignup.enabled" ||
						item === "company.flexiShiftSignup.minShiftHours" ||
						item === "shiftAcceptanceCutoff.enabled" ||
						item === "shiftAcceptanceCutoff.minHours" ||
						item === "timeOff.allowPastDates" ||
						item === "consecutiveShiftGap.skipGap.enabled" ||
						item === "consecutiveShiftGap.skipGap.maxMinutes" ||
						item in SHIFT_CHANGE_SETTING_TYPES
					) {
						obj = {
							...(obj || {
								code: item,
								type: SHIFT_CHANGE_SETTING_TYPES[item] ??
									(item.includes("minShiftHours") ||
									item === "shiftAcceptanceCutoff.minHours"
										? "System.Double"
										: item === "consecutiveShiftGap.skipGap.maxMinutes"
										? "System.Int32"
										: item.includes("triggerDays")
										? "string"
										: "System.Boolean"),
							}),
							entity: entity,
							entityId: entity === "location" ? locationData.id : companyId,
						};
					}

					let value = newShiftSettings[key];
					if (key === "minWeekHoursNotifTriggerDays" && Array.isArray(value)) {
						value = value.join(",");
					}

					if (key === "minFlexiShiftHours") {
						value = value;
					}

					// The settings API stores these as strings ("True", "24").
					if (key === "shiftChangeEnabled") {
						value = value ? "True" : "False";
					}
					if (
						key === "shiftChangeMaxPerMonth" ||
						key === "shiftChangeMinNoticeHours"
					) {
						value = String(value);
					}

					return obj ? { ...obj, value } : null;
				})
				.filter(Boolean);

			const changedRules = findDifferentKeys(
				shiftSettingsLocationData,
				newShiftSettingsLocationData
			);

			const allocationSettingsChanged =
				JSON.stringify(shiftSettingsLocationData?.allocationSettings) !==
				JSON.stringify(newShiftSettingsLocationData?.allocationSettings);

			if (changedValues.length > 0) {
				await dispatch(updateCompanySettings(changedValues));
			}

			if (changedRules.length > 0 || allocationSettingsChanged) {
				await dispatch(updateLocationData(newShiftSettingsLocationData));
			}

			if (
				newShiftSettings.overlappingShiftAllowed &&
				(stringifiedNewOverlap !== stringifiedOverlap ||
					!shiftSettings.overlappingShiftAllowed)
			) {
				await new Promise((resolve) => setTimeout(resolve, 1000));
				await dispatch(
					updateAllowedWorkCentersForShifts({
						companyId: currentLocationData?.companyId,
						locationId: currentLocationData?.id,
						workcenterIds: newOverlapWorkcenters,
						SettingTypeCode: "overlappingShiftSingup.Enabled",
					})
				);
			}

			if (
				newShiftSettings.partialShiftAllowed &&
				(stringifiedNewPartialShift !== stringifiedPartialShift ||
					!shiftSettings.partialShiftAllowed)
			) {
				await new Promise((resolve) => setTimeout(resolve, 1000));
				await dispatch(
					updateAllowedWorkCentersForShifts({
						companyId: currentLocationData?.companyId,
						locationId: currentLocationData?.id,
						workcenterIds: newPartialShiftWorkcenters,
						SettingTypeCode: "partialShiftSignup.enabled",
					})
				);
			}

			if (
				newShiftSettings.flexiShiftAllowed &&
				(stringifiedNewFlexiShift !== stringifiedFlexiShift ||
					!shiftSettings.flexiShiftAllowed)
			) {
				await new Promise((resolve) => setTimeout(resolve, 1000));
				await dispatch(
					updateAllowedWorkCentersForShifts({
						companyId: currentLocationData?.companyId,
						locationId: currentLocationData?.id,
						workcenterIds: newFlexiShiftWorkcenters,
						SettingTypeCode: "flexiShiftSignup.enabled",
					})
				);
			}

			if (
				newShiftSettings.acceptanceCutoffEnabled &&
				(stringifiedNewAcceptanceCutoff !== stringifiedAcceptanceCutoff ||
					!shiftSettings.acceptanceCutoffEnabled)
			) {
				await new Promise((resolve) => setTimeout(resolve, 1000));
				await dispatch(
					updateAllowedWorkCentersForShifts({
						companyId: currentLocationData?.companyId,
						locationId: currentLocationData?.id,
						workcenterIds: newAcceptanceCutoffWorkcenters,
						SettingTypeCode: "shiftAcceptanceCutoff.enabled",
					})
				);
			}
		} catch (error) {
			console.error("Error saving shift settings:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (Object.keys(settingsObj).length > 0) {
			const obj = {
				swapEnabled: settingsObj["shift.swapEnabled"].value,
				swapApprovalRequired:
					settingsObj["shift.swapApprovalRequired"]?.value ?? false,
				cancelEnabled: settingsObj["shift.cancelEnabled"].value,
				forceCancelEnabled:
					settingsObj["shift.forceCancelEnabled"]?.value ?? false,
				cancelWindowBeforeStartDays:
					settingsObj["shift.cancelWindowBeforeStartDays"]?.value ?? 0,
				cancelWindowAfterAcceptHrs:
					settingsObj["shift.cancelWindowAfterAcceptHrs"]?.value ?? 0,
				jobExpiryEnabled: settingsObj["clientApp.jobExpiryEnabled"].value,
				minWorkHoursRequired:
					settingsObj["jobExpiry.minWorkHoursRequired"]?.value ?? 0,
				minWorkHoursDays: settingsObj["jobExpiry.minWorkHoursDays"]?.value ?? 0,
				forceAssignShift:
					settingsObj["assignShiftToWorker.enabled"]?.value ?? false,
				forceAssignShiftBeforeStartHours:
					settingsObj["assignShiftToWorker.hrsBeforeShift"]?.value ?? 0,
				shiftPurpose:
					settingsObj["clientApp.showShiftPurpose.enabled"]?.value ?? false,
				overlappingShiftAllowed:
					settingsObj["overlappingShiftSingup.Enabled"]?.value ?? false,
				overlapWindowTime:
					settingsObj["overlappingShiftSingup.maxAllowedTime"]?.value ?? 30,
				// Note: This is not about consecutive Shifts, it is a gap between two shifts. API key based on BE.
				minShiftsGapEnabled:
					settingsObj["consecutiveShiftGap.enabled"]?.value ?? false,
				minShiftsGapHours:
					settingsObj["consecutiveShiftGap.minHours"]?.value ?? 1,
				minShiftsGapSkipEnabled:
					settingsObj["consecutiveShiftGap.skipGap.enabled"]?.value ?? false,
				minShiftsGapSkipMinutes: Number(
					settingsObj["consecutiveShiftGap.skipGap.maxMinutes"]?.value ?? 0
				),
				partialShiftAllowed:
					settingsObj["partialShiftSignup.enabled"]?.value ?? false,
				minPartialShiftHours:
					settingsObj["partialShiftSignup.minShiftHours"]?.value ??
					minShiftDurationHours,
				flexiShiftAllowed:
					(entity === "company"
						? settingsObj["company.flexiShiftSignup.enabled"]?.value
						: settingsObj["flexiShiftSignup.enabled"]?.value) ?? false,
				minFlexiShiftHours:
					(entity === "company"
						? settingsObj["company.flexiShiftSignup.minShiftHours"]?.value
						: settingsObj["flexiShiftSignup.minShiftHours"]?.value) ??
					minShiftDurationHours,
				shiftWorkerTypeFilterEnabled:
					settingsObj["shift.workerTypeFilter.enabled"]?.value ?? false,
				acceptanceCutoffEnabled:
					settingsObj["shiftAcceptanceCutoff.enabled"]?.value ?? false,
				acceptanceCutoffMinHours:
					settingsObj["shiftAcceptanceCutoff.minHours"]?.value == null
						? null
						: Number(settingsObj["shiftAcceptanceCutoff.minHours"].value),
				minWeekHoursNotifEnabled:
					settingsObj["minWeekHoursNotif.enabled"]?.value ?? false,
				pastDatedTimeOffAllowed:
					settingsObj["timeOff.allowPastDates"]?.value ?? false,
				shiftChangeEnabled: toBoolean(
					settingsObj["shiftChange.enabled"]?.value ?? false
				),
				shiftChangeMaxPerMonth: toInteger(
					settingsObj["shiftChange.maxPerMonth"]?.value,
					SHIFT_CHANGE_DEFAULT_MAX_PER_MONTH
				),
				shiftChangeMinNoticeHours: toInteger(
					settingsObj["shiftChange.minNoticeHours"]?.value,
					SHIFT_CHANGE_DEFAULT_MIN_NOTICE_HOURS
				),
				minWeekHoursNotifTriggerDays: (() => {
					const val = settingsObj["minWeekHoursNotif.triggerDays"]?.value;
					let parsedVal = val;
					if (typeof val === "string") {
						try {
							parsedVal = JSON.parse(val);
						} catch (e) {
							parsedVal = val.split(",").map((s: string) => s.trim()).filter(Boolean);
						}
					}
					if (Array.isArray(parsedVal)) {
						return parsedVal.filter((day: any) => daysOfWeek.includes(day));
					}
					return [];
				})(),
			};
			setShiftSettings(obj);
			setNewShiftSettings(obj);
		}
	}, [settingsObj, entity, minShiftDurationHours]);

	useEffect(() => {
		if (updateCompanySettingsStatus === "fulfilled") {
			setShiftSettings(newShiftSettings);
			setLoading(false);
		}
	}, [updateCompanySettingsStatus, newShiftSettings]);

	useEffect(() => {
		if (updateLocationDataStatus === "fulfilled") {
			setNewShiftSettingsLocationData(shiftSettingsLocationData);
			setLoading(false);
		}
	}, [updateLocationDataStatus, shiftSettingsLocationData]);

	useEffect(() => {
		if (updateAllowedWorkCentersForShiftsStatus === "fulfilled") {
			setOverlapWorkcenters(newOverlapWorkcenters);
			setPartialShiftWorkcenters(newPartialShiftWorkcenters);
			setFlexiShiftWorkcenters(newFlexiShiftWorkcenters);
			setAcceptanceCutoffWorkcenters(newAcceptanceCutoffWorkcenters);
			setLoading(false);
		}
	}, [
		updateAllowedWorkCentersForShiftsStatus,
		newOverlapWorkcenters,
		newPartialShiftWorkcenters,
		newFlexiShiftWorkcenters,
		newAcceptanceCutoffWorkcenters,
	]);

	useEffect(() => {
		if (locationData) {
			setShiftSettingsLocationData(locationData);
			setNewShiftSettingsLocationData(locationData);
		}
	}, [locationData]);

	return [
		{
			shiftSettings,
			newShiftSettings,
			updateCompanySettingsStatus,
			updateLocationDataStatus,
			shiftSettingsLocationData,
			newShiftSettingsLocationData,
			currentLocationData,
			loading,
			overlapWorkcenters,
			newOverlapWorkcenters,
			partialShiftWorkcenters,
			newPartialShiftWorkcenters,
			flexiShiftWorkcenters,
			newFlexiShiftWorkcenters,
			acceptanceCutoffWorkcenters,
			newAcceptanceCutoffWorkcenters,
			getCompanySettingsStatus,
			getLocationSettingsStatus,
		},
		{
			setNewShiftSettings,
			setShiftSettingsLocationData,
			setNewShiftSettingsLocationData,
			handleSave,
			setOverlapWorkcenters,
			setNewOverlapWorkcenters,
			setPartialShiftWorkcenters,
			setNewPartialShiftWorkcenters,
			setFlexiShiftWorkcenters,
			setNewFlexiShiftWorkcenters,
			setAcceptanceCutoffWorkcenters,
			setNewAcceptanceCutoffWorkcenters,
		},
	];
};

export default useShiftSettingsController;
